import { useState } from "react";
import axios from "axios";
import { Button } from "./components/ui/button";
import { Input } from "./components/ui/input";
import { Label } from "./components/ui/label";
import { toast } from "sonner";
import { ShieldCheck, KeyRound, Plug, ExternalLink, Copy, CheckCircle2, Lock } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Step = ({ n, icon: Icon, title, done, children }) => (
  <div className="flex gap-3" data-testid={`gmail-wizard-step-${n}`}>
    <div className={`h-8 w-8 rounded-full flex items-center justify-center shrink-0 text-sm font-semibold ${done ? "bg-emerald-500 text-white" : "bg-muted text-foreground"}`}>
      {done ? <CheckCircle2 className="h-4 w-4" /> : n}
    </div>
    <div className="flex-1 space-y-2 pb-5 border-b border-border/50 last:border-0">
      <p className="text-sm font-semibold flex items-center gap-1.5"><Icon className="h-4 w-4 text-muted-foreground" />{title}</p>
      {children}
    </div>
  </div>
);

export const GmailWizard = ({ defaultEmail = "", onConnected }) => {
  const [step1, setStep1] = useState(false);
  const [step2, setStep2] = useState(false);
  const [email, setEmail] = useState(defaultEmail);
  const [pwd, setPwd] = useState("");
  const [busy, setBusy] = useState(false);
  const clean = pwd.replace(/\s+/g, "");

  const openLink = (url, mark) => { window.open(url, "_blank", "noopener"); mark(true); };
  const copyName = async () => {
    try { await navigator.clipboard.writeText("Aiclo CRM"); toast.success("Nome copiato: incollalo su Google"); }
    catch { toast.info("Scrivi come nome: Aiclo CRM"); }
  };

  const connect = async () => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/csm/gmail/connect-app-password`, { email, app_password: clean });
      toast.success(r.data.smtp_ok ? "Gmail collegata: lettura email e invio riepiloghi attivi" : "Gmail collegata (lettura). Invio riepiloghi non disponibile");
      onConnected();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Collegamento non riuscito");
    }
    setBusy(false);
  };

  return (
    <div className="space-y-4 text-left" data-testid="gmail-wizard">
      <Step n={1} icon={ShieldCheck} title="Attiva la verifica in 2 passaggi" done={step1}>
        <p className="text-xs text-muted-foreground">Google la richiede per creare password per le app. Se è già attiva, salta questo passaggio.</p>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" className="h-8 text-xs gap-1" onClick={() => openLink("https://myaccount.google.com/signinoptions/twosv", setStep1)} data-testid="gmail-wizard-2fa-btn">
            Apri impostazioni Google <ExternalLink className="h-3 w-3" />
          </Button>
          <Button size="sm" variant="ghost" className="h-8 text-xs" onClick={() => setStep1(true)} data-testid="gmail-wizard-2fa-done">È già attiva</Button>
        </div>
      </Step>
      <Step n={2} icon={KeyRound} title="Crea una password per le app" done={step2}>
        <p className="text-xs text-muted-foreground">
          Nella pagina che si apre scrivi come nome <button onClick={copyName} className="font-mono font-semibold text-foreground bg-muted px-1.5 rounded inline-flex items-center gap-1" data-testid="gmail-wizard-copy-name">Aiclo CRM <Copy className="h-3 w-3" /></button>, premi <b>Crea</b> e copia il codice di 16 lettere che compare.
        </p>
        <Button size="sm" variant="outline" className="h-8 text-xs gap-1" onClick={() => openLink("https://myaccount.google.com/apppasswords", setStep2)} data-testid="gmail-wizard-apppwd-btn">
          Crea password per le app <ExternalLink className="h-3 w-3" />
        </Button>
      </Step>
      <Step n={3} icon={Plug} title="Incolla e collega" done={false}>
        <div className="grid sm:grid-cols-2 gap-2">
          <div className="space-y-1">
            <Label className="text-xs">La tua email Gmail</Label>
            <Input className="h-9" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="nome@aiclo.io" data-testid="gmail-wizard-email" />
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Password per le app (16 lettere)</Label>
            <Input className="h-9 font-mono" type="password" value={pwd} onChange={(e) => setPwd(e.target.value)} placeholder="xxxx xxxx xxxx xxxx" data-testid="gmail-wizard-password" />
          </div>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <Button className="gap-2" onClick={connect} disabled={busy || !email.includes("@") || clean.length !== 16} data-testid="gmail-wizard-connect-btn">
            <Plug className="h-4 w-4" /> {busy ? "Verifica in corso..." : "Collega e verifica"}
          </Button>
          <span className="text-[11px] text-muted-foreground flex items-center gap-1"><Lock className="h-3 w-3" /> Salvata cifrata · revocabile da Google in ogni momento</span>
        </div>
      </Step>
    </div>
  );
};
